import CONFIG from "@/assets/js/config.js";
import Vue from "vue";

export const MultiFileService = {

  /**
   * Open file input dialog
   * 
   * @param {string} id
   */
  chooseFiles(id) {
    const input = document.getElementById(id);
    if (input) input.click();
  },

  /**
   * Handle image file selection for multi-file input
   * 
   * @param {Object} options
   * @param {Event} options.event
   * @param {Array} options.newFiles
   * @param {Array} options.existingFiles
   * @param {Array} options.allowedFileTypes
   * @param {number} options.maxFileSize
   * @param {number} options.maxFileCount
   * @param {Object} options.refs
   */
  onFileChange(options) {
    const {
      event,
      newFiles = [],
      existingFiles = [],
      allowedFileTypes = [],
      maxFileSize = 0,
      maxFileCount = 0,
      refs = {}
    } = options;

    const selectedFiles = Array.from(event.target.files);
    const control = { stopProcessing: false };

    const totalIncomingCount = newFiles.length + existingFiles.length + selectedFiles.length;
    
    if (maxFileCount > 0 && totalIncomingCount > maxFileCount) {
      const template = CONFIG.FILE_SERVICE.MAX_FILE_COUNT_ERROR;
      const message = template.replace('{max}', maxFileCount);
      this.notifyFileError(message, event, control);

      return;
    }

    selectedFiles.forEach(file => {
      if (control.stopProcessing) return;

      if (!this.isFileTypeAllowed(file, allowedFileTypes)) {
        this.notifyFileError(CONFIG.FILE_SERVICE.FILE_TYPE_ERROR, event, control);
        return;
      }

      const isDuplicate = newFiles.some(f => f.name === file.name);
      if (isDuplicate) {
        this.notifyFileError(CONFIG.FILE_SERVICE.DUPLICATE_ERROR, event, control);
        return;
      }

      const maxSizeBytes = maxFileSize * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        const template = CONFIG.FILE_SERVICE.MAX_FILE_SIZE_ERROR;
        const message = template.replace('{max}', maxFileSize);
        this.notifyFileError(message, event, control);
        return;
      }

      let totalImgCount = newFiles.length + existingFiles.length;
      if (totalImgCount >= maxFileCount) {
        const template = CONFIG.FILE_SERVICE.MAX_FILE_COUNT_ERROR;
        const message = template.replace('{max}', maxFileCount);
        this.notifyFileError(message, event, control);
        return;
      }

      const index = newFiles.length;
      newFiles.push(file);

      const reader = new FileReader();
      reader.onload = () => {
        let refName = newFiles[index].name;
        if (refs[refName] && refs[refName][0]) {
          refs[refName][0].src = reader.result;
        }
        newFiles[index].data = reader.result;
      };
      reader.readAsDataURL(file);
      event.target.value = "";
    });
  },

  /**
   * Remove a new file
   * 
   * @param {number} index
   * @param {Array} fileArray
   */
  removeFile(index, fileArray) {
    fileArray.splice(index, 1);
  },

  /**
   * Check if a file type is allowed
   * 
   * @param {File} file
   * @param {Array} allowedFileTypes
   * @returns {boolean}
   */
  isFileTypeAllowed(file, allowedFileTypes) {
    if (!file || !file.type || !Array.isArray(allowedFileTypes)) return false;

    // Special case: allow any image
    if (allowedFileTypes.includes("image/*") && file.type.startsWith("image/")) {
      return true;
    }

    // Exact match
    return allowedFileTypes.includes(file.type);
  },

  /**
   * Show error notification and optionally stop processing
   * 
   * @param {string} message
   * @param {Event} event
   * @param {Object} control
   */
  notifyFileError(message, event, control) {
    Vue.prototype.$notification.error(message, { timer: 3, position: "bottomCenter" });
    if (event) event.target.value = "";
    if (control) control.stopProcessing = true;
  },
};
